import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * FASE I — Wizard Infortuni (Famiglia).
 * Step 0: Attività / esposizione al rischio
 * Step 1: Massimali + familiari da coprire (ArrayField)
 */

const ATTIVITA_PROFESSIONALE = [
  "Impiegato / ufficio",
  "Operaio",
  "Tecnico / manutentore",
  "Personale sanitario",
  "Insegnante",
  "Commerciante",
  "Libero professionista",
  "Altro",
];

const HOBBY_A_RISCHIO = [
  "Sci / snowboard",
  "Motociclismo",
  "Arti marziali",
  "Alpinismo / arrampicata",
  "Immersioni subacquee",
  "Volo (parapendio / paracadutismo)",
  "Sport equestri",
  "Ciclismo agonistico",
];

export function InfortuniStep0({ formData, update, toggleArray }) {
  return (
    <div className="space-y-5" data-testid="infortuni-step-0">
      <p className="text-sm text-[#4A5E51]">
        Le coperture Infortuni valutano la tua esposizione al rischio in base ad attività lavorativa e hobby.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Età</Label>
          <Input
            type="number" min={16} max={90}
            value={formData.eta || ""}
            onChange={e => update("eta", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11"
            placeholder="35"
            data-testid="infortuni-eta"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Attività professionale <span className="text-[#D97B58]">*</span></Label>
          <Select value={formData.attivita_professionale || ""}
                  onValueChange={v => update("attivita_professionale", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="infortuni-attivita-select">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              {ATTIVITA_PROFESSIONALE.map(a => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Descrizione mansione (facoltativa)</Label>
        <Input
          value={formData.mansione || ""}
          onChange={e => update("mansione", e.target.value)}
          className="rounded-xl border-[#E5E2DB] h-11"
          placeholder="es. Elettricista industriale, uso di macchinari pesanti"
          data-testid="infortuni-mansione"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">
          Hai hobby o attività sportive a rischio?
        </Label>
        <p className="text-xs text-[#4A5E51]">Seleziona tutte quelle che pratichi (anche occasionalmente).</p>
        <div className="space-y-2 pt-1">
          {HOBBY_A_RISCHIO.map(h => (
            <label
              key={h}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all text-sm ${
                (formData.hobby_rischio || []).includes(h)
                  ? "border-[#163321] bg-[#163321]/5"
                  : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
              }`}
              data-testid={`infortuni-hobby-${h.replace(/[^a-z]/gi, "").toLowerCase()}`}
            >
              <Checkbox
                checked={(formData.hobby_rischio || []).includes(h)}
                onCheckedChange={() => toggleArray("hobby_rischio", h)}
                className="data-[state=checked]:bg-[#163321]"
              />
              <span className="text-[#0F1F14]">{h}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Hai già subito infortuni gravi in passato?</Label>
        <RadioGroup
          value={formData.infortuni_precedenti || ""}
          onValueChange={v => update("infortuni_precedenti", v)}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="inf-prev-no" data-testid="infortuni-prev-no" />
            <Label htmlFor="inf-prev-no" className="text-sm">No</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="si" id="inf-prev-si" data-testid="infortuni-prev-si" />
            <Label htmlFor="inf-prev-si" className="text-sm">Sì</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

const MASSIMALI = [
  { v: 50000,  l: "50.000 €" },
  { v: 100000, l: "100.000 €" },
  { v: 200000, l: "200.000 €" },
  { v: 300000, l: "300.000 €" },
  { v: 500000, l: "500.000 €" },
];

const FAMILIARE_TIPI = ["Coniuge/convivente", "Figlio", "Genitore convivente", "Altro"];

export function InfortuniStep1({ formData, update }) {
  const familiari = formData.familiari_coperti || [];

  const addFamiliare = () => {
    update("familiari_coperti", [
      ...familiari,
      { tipo: "", nome: "", cognome: "", anno_nascita: "" },
    ]);
  };
  const updateFamiliare = (idx, field, val) => {
    const next = [...familiari];
    next[idx] = { ...next[idx], [field]: val };
    update("familiari_coperti", next);
  };
  const removeFamiliare = (idx) => {
    update("familiari_coperti", familiari.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-5" data-testid="infortuni-step-1">
      <p className="text-sm text-[#4A5E51]">Definisci i massimali di copertura desiderati.</p>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Massimale Invalidità Permanente <span className="text-[#D97B58]">*</span></Label>
        <Select
          value={formData.massimale_invalidita ? String(formData.massimale_invalidita) : ""}
          onValueChange={v => update("massimale_invalidita", parseInt(v))}
        >
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="infortuni-massimale-invalidita">
            <SelectValue placeholder="Seleziona" />
          </SelectTrigger>
          <SelectContent>
            {MASSIMALI.map(m => (
              <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Massimale Morte da infortunio</Label>
        <Select
          value={formData.massimale_morte ? String(formData.massimale_morte) : ""}
          onValueChange={v => update("massimale_morte", parseInt(v))}
        >
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="infortuni-massimale-morte">
            <SelectValue placeholder="Seleziona" />
          </SelectTrigger>
          <SelectContent>
            {MASSIMALI.map(m => (
              <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Diaria giornaliera da ricovero (€/giorno)</Label>
        <Select
          value={formData.diaria_ricovero ? String(formData.diaria_ricovero) : ""}
          onValueChange={v => update("diaria_ricovero", parseInt(v))}
        >
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="infortuni-diaria">
            <SelectValue placeholder="Seleziona" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Nessuna diaria</SelectItem>
            <SelectItem value="50">50 €/giorno</SelectItem>
            <SelectItem value="100">100 €/giorno</SelectItem>
            <SelectItem value="150">150 €/giorno</SelectItem>
            <SelectItem value="200">200 €/giorno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Familiari (ArrayField) */}
      <div className="pt-2 border-t border-[#E5E2DB]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-sm text-[#0F1F14]">Familiari da includere in polizza</Label>
            <p className="text-xs text-[#4A5E51]">Opzionale — puoi aggiungere fino a 6 familiari.</p>
          </div>
          <Button
            type="button" variant="outline" size="sm"
            onClick={addFamiliare}
            disabled={familiari.length >= 6}
            className="rounded-full border-[#163321]/40 text-[#163321] hover:bg-[#163321]/5"
            data-testid="infortuni-add-familiare-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
        {familiari.length === 0 && (
          <p className="text-xs italic text-[#4A5E51]">Nessun familiare aggiunto — la polizza copre solo te.</p>
        )}
        <div className="space-y-3">
          {familiari.map((f, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl border border-[#E5E2DB] bg-[#FCFCFA] space-y-3"
              data-testid={`infortuni-familiare-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#4A5E51] uppercase tracking-wide">
                  Familiare {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeFamiliare(idx)}
                  className="text-[#D97B58] hover:text-[#C06A48]"
                  data-testid={`infortuni-familiare-rm-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Grado di parentela</Label>
                  <Select
                    value={f.tipo || ""}
                    onValueChange={v => updateFamiliare(idx, "tipo", v)}
                  >
                    <SelectTrigger className="h-10 rounded-xl mt-1" data-testid={`infortuni-fam-tipo-${idx}`}>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      {FAMILIARE_TIPI.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Anno di nascita</Label>
                  <Input
                    type="number" min={1920} max={2025}
                    value={f.anno_nascita || ""}
                    onChange={e => updateFamiliare(idx, "anno_nascita", e.target.value)}
                    className="h-10 rounded-xl mt-1"
                    placeholder="1985"
                    data-testid={`infortuni-fam-anno-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Nome</Label>
                  <Input
                    value={f.nome || ""}
                    onChange={e => updateFamiliare(idx, "nome", e.target.value)}
                    className="h-10 rounded-xl mt-1"
                    data-testid={`infortuni-fam-nome-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Cognome</Label>
                  <Input
                    value={f.cognome || ""}
                    onChange={e => updateFamiliare(idx, "cognome", e.target.value)}
                    className="h-10 rounded-xl mt-1"
                    data-testid={`infortuni-fam-cognome-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function renderInfortuniStep(step, formData, update, toggleArray) {
  if (step === 0) return <InfortuniStep0 formData={formData} update={update} toggleArray={toggleArray} />;
  if (step === 1) return <InfortuniStep1 formData={formData} update={update} />;
  return null;
}
